/**
 * Admin system connection details.
 *
 * Fetched from the sidecar at startup rather than baked in at build time, so one
 * image can serve any environment. `loadIoTCoreAdminSystemInfo` is awaited in
 * src/index.tsx before React mounts, which is why every consumer can keep reading
 * these fields synchronously.
 *
 * Do not copy these values at module scope — they are empty until the bootstrap fetch
 * resolves. Read them inside functions, components or interceptors.
 */
export const IoTCoreAdminSystemInfo = {
  URL: '',
  systemKey: '',
  regionList: [] as string[],
};

interface SidecarConfigResponse {
  adminUrl: string;
  adminSystemKey: string;
  regions: string[];
}

/**
 * Fetches runtime config from the sidecar and populates IoTCoreAdminSystemInfo in
 * place. Rejects if the sidecar cannot be reached or answers with something unusable,
 * since there is nothing meaningful the app can do without an admin system to talk to.
 */
export async function loadIoTCoreAdminSystemInfo(): Promise<void> {
  const response = await fetch('/iotcore/config');
  if (!response.ok) {
    throw new Error(`Failed to load configuration from the sidecar (HTTP ${response.status})`);
  }

  const config = (await response.json()) as SidecarConfigResponse;
  if (!config.adminUrl || !config.adminSystemKey) {
    throw new Error('The sidecar returned a configuration with no admin URL or system key');
  }

  IoTCoreAdminSystemInfo.URL = config.adminUrl;
  IoTCoreAdminSystemInfo.systemKey = config.adminSystemKey;
  IoTCoreAdminSystemInfo.regionList = config.regions ?? [];
}
